import { Form } from "@/components/form/form";
import { FormField } from "@/components/form/form-field/form-field";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { IconButton } from "@/components/ui/icon-button";
import { useAuth } from "@/hooks/auth/auth";
import { toast } from "sonner";
import { useAuthResetPasword } from "@/orval/auth";
import type { ResetPasswordDTO } from "@/orval/model";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Key, ArrowLeft, CheckCircle } from "lucide-react";
import { useLayoutEffect, useState } from "react";
import { z } from "zod";

const resetPasswordSearchSchema = z.object({
  email: z.string().optional(),
  token: z.string().optional(),
});

interface ResetPasswordFormData {
  newPassword: string;
  newPasswordConfirmation: string;
}

const ResetPassword = () => {
  const { mutateAsync: resetPasswordAsync, isPending } = useAuthResetPasword();
  const { setToken } = useAuth();
  const navigate = useNavigate();
  const { email, token } = Route.useSearch();
  const [passwordReset, setPasswordReset] = useState(false);
  const [didLogIn, setDidLogin] = useState(false);

  useLayoutEffect(() => {
    if (didLogIn) {
      // Redirect to my organizations page after successful reset
      navigate({ to: "/my/organizations" });
    }
  }, [didLogIn, navigate]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!email || !token) {
      toast.error("Invalid reset link. Please request a new password reset.");
      return;
    }

    const resetData: ResetPasswordDTO = {
      email,
      token,
      newPassword: data.newPassword,
      newPasswordConfirmation: data.newPasswordConfirmation,
    };

    const response = await resetPasswordAsync({ data: resetData });

    if (response?.token) {
      setToken(response.token);
      setPasswordReset(true);
      toast.success("Your password has been reset successfully.");
      setDidLogin(true);
    }
  };

  if (!email || !token) {
    return (
      <div className="flex items-center justify-center h-full flex-1">
        <Card className="max-w-full w-96">
          <CardHeader>
            <CardTitle>Invalid Reset Link</CardTitle>
            <CardDescription>This password reset link is invalid or has expired.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Please request a new password reset to continue.</p>
          </CardContent>
          <CardFooter className="flex flex-col gap-2">
            <IconButton className="w-full" icon={<Key />} onClick={() => navigate({ to: "/forgot-password" })}>
              Request New Reset
            </IconButton>
            <Link
              to="/login"
              search={{ redirect: undefined, message: undefined }}
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              <ArrowLeft className="size-4" />
              Back to Login
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (passwordReset) {
    return (
      <div className="flex items-center justify-center h-full flex-1">
        <Card className="max-w-full w-96">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="size-5 text-green-500" />
              Password Reset Complete
            </CardTitle>
            <CardDescription>Your password has been successfully reset and you are now logged in.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">You will be redirected to your dashboard shortly…</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full flex-1">
      <Card className="max-w-full w-96">
        <CardHeader>
          <CardTitle>Reset Password</CardTitle>
          <CardDescription>Enter your new password below.</CardDescription>
        </CardHeader>
        <CardContent>
          <Form<ResetPasswordFormData>
            onSubmit={onSubmit}
            useFormProps={{
              defaultValues: {
                newPassword: "",
                newPasswordConfirmation: "",
              },
            }}
          >
            <FormField
              label="New Password"
              name="newPassword"
              inputProps={{
                placeholder: "Enter your new password",
                autoFocus: true,
                autoComplete: "new-password",
                type: "password",
              }}
              rules={{
                required: "Password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters long",
                },
              }}
            />

            <FormField
              label="Confirm New Password"
              name="newPasswordConfirmation"
              inputProps={{
                placeholder: "Confirm your new password",
                autoComplete: "new-password",
                type: "password",
              }}
              rules={{
                required: "Password confirmation is required",
                validate: (value, { newPassword }) => value === newPassword || "Passwords do not match",
              }}
            />

            <IconButton className="w-full" type="submit" icon={<Key />} disabled={isPending}>
              {isPending ? "Resetting Password..." : "Reset Password"}
            </IconButton>
          </Form>
        </CardContent>
        <CardFooter className="flex justify-center">
          <Link
            to="/login"
            search={{ redirect: undefined, message: undefined }}
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            <ArrowLeft className="size-4" />
            Back to Login
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
};

export const Route = createFileRoute("/account/reset-password")({
  validateSearch: resetPasswordSearchSchema,
  component: ResetPassword,
});
